import { useCallback, useMemo } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  DeduplicationGroupingConfig,
  DeduplicationRule,
  DeduplicationSettings,
  DedupStrategy,
  FieldSuggestion,
  GroupingSettings,
  GroupingStrategy,
  MatchStrategy,
} from '../../types/pipelineConfig';
import { getDefaultDeduplicationConfig, getNormalizedFieldSuggestions } from '../../utils/pipelineDefaults';
import { FieldTagList } from './FieldTagList';
import { StepConfigHeader } from './StepConfigHeader';
import { StrategySelector } from './StrategySelector';
import type { StrategyOption } from './StrategySelector';
import { TimeWindowInput } from './TimeWindowInput';

// ── Props ──────────────────────────────────────────────────────────────────────

interface DeduplicationGroupingConfigEditorProps {
  config?: DeduplicationGroupingConfig;
  onChange: (config: DeduplicationGroupingConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
  /**
   * Full list of upstream field suggestions (typically produced by upstream Normalization
   * and Enrichment steps). When provided, replaces the built-in hardcoded suggestion list
   * inside each deduplication rule's field picker. If omitted, falls back to the built-in
   * base normalized field suggestions.
   */
  upstreamFields?: FieldSuggestion[];
}

// ── ID generation ──────────────────────────────────────────────────────────────

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

// ── Strategy options ───────────────────────────────────────────────────────────

const DEDUP_STRATEGY_OPTIONS: StrategyOption<DedupStrategy>[] = [
  {
    value: 'externalId',
    label: 'External ID Match',
    icon: '🔑',
    description:
      "Match by the alert provider's unique identifier. Simplest strategy — uses the ID assigned by the source system.",
  },
  {
    value: 'composite',
    label: 'Composite Key',
    icon: '🧩',
    description:
      'Build a deduplication key from multiple alert fields. Most flexible — define exactly which fields identify a unique alert.',
  },
  {
    value: 'contentHash',
    label: 'Content Hash',
    icon: '#️⃣',
    description:
      'Hash the entire alert payload to find exact duplicates. Strictest matching — any field difference creates a new alert.',
  },
];

const DEDUP_RULE_STRATEGY_OPTIONS: StrategyOption<DedupStrategy>[] = [
  {
    value: 'externalId',
    label: 'External ID Match',
    icon: '🔑',
    description: 'Match by the normalized ExternalAlertId',
  },
  {
    value: 'contentHash',
    label: 'Content Hash',
    icon: '#️⃣',
    description: 'Match by computing a hash of selected fields',
  },
  {
    value: 'composite',
    label: 'Composite Key',
    icon: '🧩',
    description: 'Match by combining multiple field values',
  },
];

const RULE_FIELD_SUGGESTIONS = [
  'normalized.source',
  'normalized.externalAlertId',
  'normalized.resourceId',
  'normalized.title',
  'normalized.monitoringService',
];

const MATCH_STRATEGY_OPTIONS: StrategyOption<MatchStrategy>[] = [
  {
    value: 'exact',
    label: 'Exact',
    description: 'Require exact match on all dedup key fields',
  },
  {
    value: 'fuzzy',
    label: 'Fuzzy',
    description:
      'Allow minor differences (case-insensitive, whitespace-normalized)',
  },
];

const GROUPING_STRATEGY_OPTIONS: StrategyOption<GroupingStrategy>[] = [
  {
    value: 'time',
    label: 'Time Window',
    icon: '⏱️',
    description:
      'Group all alerts arriving within a time window into the same incident. Simple but may over-group unrelated alerts.',
  },
  {
    value: 'content',
    label: 'Content Match',
    icon: '📋',
    description:
      'Group alerts with matching field values. Alerts must share the same values for selected fields to be grouped together.',
  },
  {
    value: 'intelligent',
    label: 'Intelligent',
    icon: '🧠',
    description:
      'Combine time and content matching. Alerts must arrive within the time window AND match on selected fields. Recommended for most setups.',
  },
];

// ── Helper components ──────────────────────────────────────────────────────────

function FieldLabel({ label, helper, htmlFor }: { label: string; helper?: string; htmlFor?: string }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {helper && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      )}
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
      {description && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );
}

// ── Section card wrapper ───────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function SectionCard({
  title,
  description,
  children,
}: SectionCardProps) {
  return (
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label={`${title} configuration`}>
      <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
        <span>{title === 'Deduplication' ? '🔑 ' : '🗂️ '}{title}</span>
      </summary>
      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        <div className="space-y-6">
          {children}
        </div>
      </div>
    </details>
  );
}

// ── Labeled field wrapper ──────────────────────────────────────────────────────

interface FieldGroupProps {
  label: string;
  helpText?: string;
  children: React.ReactNode;
}

function FieldGroup({ label, helpText, children }: FieldGroupProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {children}
      {helpText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}
    </div>
  );
}

// ── Flow connector between sections ────────────────────────────────────────────

function FlowConnector() {
  return (
    <div
      className="flex flex-col items-center gap-1 py-2"
      aria-hidden="true"
    >
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
      <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
        <svg
          className="h-3 w-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
        followed by
      </div>
      <p className="text-[10px] text-gray-400 dark:text-gray-500">
        Deduplication runs first, then grouping evaluates non-duplicate alerts
      </p>
      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
    </div>
  );
}

// ── Sortable rule card ─────────────────────────────────────────────────────────

interface SortableRuleCardProps {
  rule: DeduplicationRule;
  index: number;
  totalRules: number;
  disabled: boolean;
  dedupDisabled: boolean;
  ruleFieldSuggestions: FieldSuggestion[];
  onUpdate: (id: string, patch: Partial<DeduplicationRule>) => void;
  onRemove: (id: string) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
}

function SortableRuleCard({
  rule,
  index,
  totalRules,
  disabled,
  dedupDisabled,
  ruleFieldSuggestions,
  onUpdate,
  onRemove,
  onMove,
}: SortableRuleCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rule.id, disabled: dedupDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const ruleDisabled = dedupDisabled || !rule.enabled;
  const showFields = rule.strategy === 'composite' || rule.strategy === 'contentHash';
  const strategyLabel =
    DEDUP_RULE_STRATEGY_OPTIONS.find((option) => option.value === rule.strategy)?.label ??
    rule.strategy;

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'z-10' : ''}>
      <details
        className={`rounded-lg border ${
          rule.enabled
            ? 'border-gray-200 dark:border-gray-700'
            : 'border-gray-200 dark:border-gray-700 opacity-60'
        } bg-white dark:bg-gray-800 ${isDragging ? 'shadow-lg ring-2 ring-indigo-500/40' : ''}`}
      >
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              {...attributes}
              {...listeners}
              disabled={dedupDisabled}
              onClick={(e) => e.preventDefault()}
              className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing"
              aria-label={`Drag rule ${rule.name || 'unnamed'} to reorder`}
              title="Drag to reorder"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M9 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm12-16a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm2 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
              </svg>
            </button>
            <span className="truncate">📋 #{index + 1} {rule.name || '(unnamed)'}</span>
          </span>
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onMove(index, 'up'); }}
              disabled={dedupDisabled || index === 0}
              className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={`Move rule ${rule.name || 'unnamed'} up`}
              title="Move rule up"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onMove(index, 'down'); }}
              disabled={dedupDisabled || index === totalRules - 1}
              className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={`Move rule ${rule.name || 'unnamed'} down`}
              title="Move rule down"
            >
              ↓
            </button>
            <span className="ml-2 text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">
              {strategyLabel} · {rule.fields.length} field{rule.fields.length === 1 ? '' : 's'}
            </span>
          </span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="mt-4 flex items-center gap-3">
            <input
              className="input flex-1"
              value={rule.name}
              onChange={(e) => onUpdate(rule.id, { name: e.target.value })}
              disabled={disabled}
              placeholder="Rule name"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 shrink-0">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => onUpdate(rule.id, { enabled: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 rounded"
              />
              Enabled
            </label>
            <button
              type="button"
              onClick={() => onRemove(rule.id)}
              disabled={disabled}
              className="shrink-0 p-1.5 rounded text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label={`Remove rule ${rule.name}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <div className="mt-4">
            <FieldLabel label="Strategy" helper="How this rule identifies duplicate alerts." />
            <StrategySelector<DedupStrategy>
              options={DEDUP_RULE_STRATEGY_OPTIONS}
              value={rule.strategy}
              onChange={(strategy) => onUpdate(rule.id, { strategy })}
              disabled={ruleDisabled}
            />
          </div>

          {rule.strategy === 'externalId' && (
            <div className="mt-4 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Matches by the normalized ExternalAlertId. No field configuration needed.
              </p>
            </div>
          )}

          {showFields && (
            <div className="mt-4">
              <FieldLabel
                label={rule.strategy === 'composite' ? 'Key Fields' : 'Hash Fields'}
                helper={
                  rule.strategy === 'composite'
                    ? 'Fields combined to form the deduplication key. Alerts matching on all fields are duplicates.'
                    : 'Fields included in the content hash. Alerts with identical hash values are duplicates.'
                }
              />
              <FieldTagList
                fields={rule.fields}
                onChange={(fields) => onUpdate(rule.id, { fields })}
                suggestions={ruleFieldSuggestions}
                placeholder={
                  rule.strategy === 'composite'
                    ? 'Add a key field…'
                    : 'Add a hash field…'
                }
                disabled={ruleDisabled}
              />
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

// ── Main editor ────────────────────────────────────────────────────────────────

export function DeduplicationGroupingConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
  upstreamFields,
}: DeduplicationGroupingConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultDeduplicationConfig(endpointType), [endpointType]);
  const config = useMemo(() => {
    const legacyConfig = (rawConfig ?? {}) as Partial<DeduplicationGroupingConfig> & {
      rules?: DeduplicationRule[];
      deduplicationRules?: DeduplicationRule[];
      groupingRules?: string[];
    };

    const deduplication = {
      ...defaultConfig.deduplication,
      ...(legacyConfig.deduplication ?? {}),
      rules:
        legacyConfig.deduplication?.rules ??
        legacyConfig.deduplicationRules ??
        legacyConfig.rules ??
        defaultConfig.deduplication.rules ??
        [],
    };

    const grouping = {
      ...defaultConfig.grouping,
      ...(legacyConfig.grouping ?? {}),
      groupByFields:
        legacyConfig.grouping?.groupByFields ??
        legacyConfig.groupingRules ??
        defaultConfig.grouping.groupByFields ??
        [],
    };

    return {
      ...defaultConfig,
      ...legacyConfig,
      deduplication,
      grouping,
    };
  }, [defaultConfig, rawConfig]);

  const rules =
    config?.deduplication?.rules ??
    ((rawConfig as { deduplicationRules?: DeduplicationRule[]; rules?: DeduplicationRule[] } | undefined)?.deduplicationRules) ??
    ((rawConfig as { deduplicationRules?: DeduplicationRule[]; rules?: DeduplicationRule[] } | undefined)?.rules) ??
    [];
  const groupingRules =
    config?.grouping?.groupByFields ??
    ((rawConfig as { groupingRules?: string[] } | undefined)?.groupingRules) ??
    [];
  const isStepEnabled = (config as DeduplicationGroupingConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const { deduplication, grouping } = config;
  // Normalize the rule list: legacy `operator: 'and'` values are coerced to `'or'` for
  // display purposes, matching the runtime behavior (backend ignores this field entirely).
  const deduplicationRules = useMemo(
    () => rules.map((r) => (r.operator === 'and' ? { ...r, operator: 'or' as const } : r)),
    [rules],
  );
  const suggestions= useMemo(() => getNormalizedFieldSuggestions(), []);

  const ruleFieldSuggestions = useMemo<FieldSuggestion[]>(() => {
    if (upstreamFields && upstreamFields.length > 0) {
      return upstreamFields;
    }
    return RULE_FIELD_SUGGESTIONS.map((f) => ({ path: f, label: f, description: '' }));
  }, [upstreamFields]);

  // ── Dedup helpers──────────────────────────────────────────────────────────

  const updateDedup = useCallback(
    (patch: Partial<DeduplicationSettings>) => {
      onChange({
        ...config,
        deduplication: { ...config.deduplication, ...patch },
      });
    },
    [config, onChange],
  );

  const handleDedupStrategyChange = useCallback(
    (strategy: DedupStrategy) => {
      updateDedup({ strategy });
    },
    [updateDedup],
  );

  const handleDedupKeyFieldsChange = useCallback(
    (dedupKeyFields: string[]) => {
      updateDedup({ dedupKeyFields });
    },
    [updateDedup],
  );

  const handleDedupTimeWindowChange = useCallback(
    (timeWindowMinutes: number) => {
      updateDedup({ timeWindowMinutes });
    },
    [updateDedup],
  );

  const handleMatchStrategyChange = useCallback(
    (matchStrategy: MatchStrategy) => {
      updateDedup({ matchStrategy });
    },
    [updateDedup],
  );

  // ── Rules helpers ──────────────────────────────────────────────────────────

  const hasRules = deduplicationRules.length > 0;

  const enableAdvancedRules = useCallback(() => {
    const { strategy, dedupKeyFields } = config.deduplication;
    const initialRule: DeduplicationRule = {
      id: createId('dedup'),
      name: 'Default Rule',
      enabled: true,
      strategy,
      fields: dedupKeyFields,
      operator: 'or',
    };
    updateDedup({ rules: [initialRule] });
  }, [config.deduplication, updateDedup]);

  const disableAdvancedRules = useCallback(() => {
    updateDedup({ rules: [] });
  }, [updateDedup]);

  const updateRule = useCallback(
    (id: string, patch: Partial<DeduplicationRule>) => {
      const rules = (config.deduplication.rules ?? []).map((r) =>
        r.id === id ? { ...r, ...patch } : r,
      );
      updateDedup({ rules });
    },
    [config.deduplication.rules, updateDedup],
  );

  const addRule = useCallback(() => {
    const rules = deduplicationRules;
    const newRuleId = createId('dedup');
    updateDedup({
      rules: [
        ...rules,
        {
          id: newRuleId,
          name: `Rule ${rules.length + 1}`,
          enabled: true,
          strategy: 'externalId',
          fields: [],
          operator: 'or',
        },
      ],
    });
  }, [deduplicationRules, updateDedup]);

  const removeRule = useCallback(
    (id: string) => {
      const rules = deduplicationRules.filter((r) => r.id !== id);
      updateDedup({ rules });
    },
    [deduplicationRules, updateDedup],
  );

  const moveRule = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= deduplicationRules.length) return;
      const reordered = [...deduplicationRules];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      updateDedup({ rules: reordered });
    },
    [deduplicationRules, updateDedup],
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleRuleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = deduplicationRules.findIndex((r) => r.id === active.id);
      const newIndex = deduplicationRules.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(deduplicationRules, oldIndex, newIndex);
      updateDedup({ rules: reordered });
    },
    [deduplicationRules, updateDedup],
  );

  // ── Grouping helpers ───────────────────────────────────────────────────────

  const updateGrouping = useCallback(
    (patch: Partial<GroupingSettings>) => {
      onChange({
        ...config,
        grouping: { ...config.grouping, ...patch },
      });
    },
    [config, onChange],
  );

  const handleGroupingStrategyChange = useCallback(
    (strategy: GroupingStrategy) => {
      updateGrouping({ strategy });
    },
    [updateGrouping],
  );

  const handleGroupByFieldsChange = useCallback(
    (groupByFields: string[]) => {
      updateGrouping({ groupByFields });
    },
    [updateGrouping],
  );

  const handleGroupingTimeWindowChange = useCallback(
    (timeWindowMinutes: number) => {
      updateGrouping({ timeWindowMinutes });
    },
    [updateGrouping],
  );

  const handleMaxAlertsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseInt(e.target.value, 10);
      if (Number.isFinite(parsed) && parsed >= 0) {
        updateGrouping({ maxAlertsPerGroup: parsed });
      }
    },
    [updateGrouping],
  );

  const handleAutoMergeResolvedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateGrouping({ autoMergeResolved: e.target.checked });
    },
    [updateGrouping],
  );

  // ── Derived state ──────────────────────────────────────────────────────────

  const showDedupKeyFields = deduplication.strategy === 'composite';
  const showExcludeFields = deduplication.strategy === 'contentHash';
  const showGroupByFields =
    grouping.strategy === 'content' || grouping.strategy === 'intelligent';
  const autoMergeId = 'auto-merge-resolved';
  const dedupWorkNoteId = 'dedup-add-work-note';
  const dedupDisabled = disabled || !deduplication.enabled;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-2">
      <StepConfigHeader
        title="Deduplication & Grouping"
        isEnabled={isStepEnabled}
        onToggleEnabled={(enabled) =>
          onChange({ ...config, isEnabled: enabled } as DeduplicationGroupingConfig & { isEnabled: boolean })
        }
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-2' : 'space-y-2 opacity-50 pointer-events-none'}>
      {/* ─── Section 1: Deduplication ─────────────────────────────────────── */}
      <SectionCard
        title="Deduplication"
        description="Define rules that determine when an incoming alert is a duplicate of an existing one. Rules are evaluated top-to-bottom in the order shown — the first matching rule wins and stops further evaluation."
      >
        {/* Advanced rules toggle */}
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Advanced Multi-Rule Deduplication
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Define a prioritized list of matching rules. Rules are evaluated top-to-bottom; the first one that matches wins.
              </p>
            </div>
            <button
              type="button"
              onClick={hasRules ? disableAdvancedRules : enableAdvancedRules}
              disabled={dedupDisabled}
              className={`shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                hasRules
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {hasRules ? 'Using Advanced Rules' : 'Use Advanced Rules'}
            </button>
          </div>

          {!hasRules && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 italic">
              Example: put an <strong>External ID</strong> rule first, then a <strong>Composite Key</strong> rule
              as a fallback for when the ID isn&rsquo;t present or matches. The first rule that finds a duplicate wins.
            </p>
          )}
        </div>

        {/* ── Legacy single-strategy UI ──────────────────────────────────── */}
        {!hasRules && (
          <>
            <FieldGroup label="Deduplication Strategy">
              <StrategySelector<DedupStrategy>
                options={DEDUP_STRATEGY_OPTIONS}
                value={deduplication.strategy}
                onChange={handleDedupStrategyChange}
                disabled={dedupDisabled}
              />
            </FieldGroup>

            {deduplication.strategy === 'externalId' && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Alerts will be deduplicated by their external provider ID. No
                  additional configuration needed.
                </p>
              </div>
            )}

            {showDedupKeyFields && (
              <FieldGroup
                label="Deduplication Key Fields"
                helpText="Select the fields that together uniquely identify an alert. Alerts with identical values for all selected fields will be considered duplicates."
              >
                <FieldTagList
                  fields={deduplication.dedupKeyFields}
                  onChange={handleDedupKeyFieldsChange}
                  suggestions={suggestions}
                  placeholder="Add a dedup key field…"
                  disabled={dedupDisabled}
                />
              </FieldGroup>
            )}

            {showExcludeFields && (
              <FieldGroup
                label="Exclude Fields"
                helpText="Fields listed here will be excluded from the hash. Use this to ignore timestamps or other fields that change between duplicate alerts."
              >
                <FieldTagList
                  fields={deduplication.dedupKeyFields}
                  onChange={handleDedupKeyFieldsChange}
                  suggestions={suggestions}
                  placeholder="Add a field to exclude…"
                  disabled={dedupDisabled}
                />
              </FieldGroup>
            )}
          </>
        )}

        {/* ── Advanced multi-rule UI ─────────────────────────────────────── */}
        {hasRules && (
          <div className="space-y-1">
            <SectionHeader
              title="Deduplication Rules"
              description="Each rule defines a matching strategy. Rules are evaluated top-to-bottom — the first match wins and stops evaluation. Drag or use the arrow buttons to reorder."
            />

            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleRuleDragEnd}>
              <SortableContext items={deduplicationRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <div className="mt-3 space-y-0">
                  {deduplicationRules.map((rule, index) => {
                    const isLast = index === deduplicationRules.length - 1;
                    return (
                      <div key={rule.id}>
                        <SortableRuleCard
                          rule={rule}
                          index={index}
                          totalRules={deduplicationRules.length}
                          disabled={disabled}
                          dedupDisabled={dedupDisabled}
                          ruleFieldSuggestions={ruleFieldSuggestions}
                          onUpdate={updateRule}
                          onRemove={removeRule}
                          onMove={moveRule}
                        />
                        {!isLast && (
                          <div className="flex justify-center py-2" aria-hidden="true">
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                              title="Rules are evaluated top-to-bottom; the first match wins."
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                              OR then try
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>

            {/* Add rule button */}
            <button
              type="button"
              onClick={addRule}
              disabled={dedupDisabled}
              className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add Deduplication Rule
            </button>
          </div>
        )}

        {/* Time window */}
        <FieldGroup
          label="Deduplication Window"
          helpText="How far back to look for duplicate alerts. Set to 0 for exact match only (no time constraint)."
        >
          <TimeWindowInput
            value={deduplication.timeWindowMinutes}
            onChange={handleDedupTimeWindowChange}
            disabled={dedupDisabled}
          />
        </FieldGroup>

        {/* Match strategy */}
        <FieldGroup
          label="Match Strategy"
          helpText="Controls how field values are compared when checking for duplicates."
        >
          <StrategySelector<MatchStrategy>
            options={MATCH_STRATEGY_OPTIONS}
            value={deduplication.matchStrategy}
            onChange={handleMatchStrategyChange}
            disabled={dedupDisabled}
          />
        </FieldGroup>

        <div className="space-y-3">
          <h4 className="text-sm font-medium">Work Notes on Duplicate</h4>
          <label className="flex items-center gap-2">
            <input
              id={dedupWorkNoteId}
              type="checkbox"
              checked={deduplication.addWorkNoteOnDuplicate ?? false}
              onChange={(e) => updateDedup({ addWorkNoteOnDuplicate: e.target.checked })}
              disabled={dedupDisabled}
            />
            <span className="text-sm">Add internal work note when duplicate detected</span>
          </label>
          {deduplication.addWorkNoteOnDuplicate && (
            <div>
              <label htmlFor={`${dedupWorkNoteId}-template`} className="text-xs text-gray-500">Note template</label>
              <textarea
                id={`${dedupWorkNoteId}-template`}
                className="w-full rounded border p-2 text-sm"
                rows={2}
                value={deduplication.duplicateWorkNoteTemplate ?? 'Duplicate event received. Source: {source}, External ID: {externalId}'}
                onChange={(e) => updateDedup({ duplicateWorkNoteTemplate: e.target.value })}
                disabled={dedupDisabled}
                placeholder="Duplicate event received. Source: {source}, External ID: {externalId}"
              />
              <p className="text-xs text-gray-400 mt-1">
                Available placeholders: {'{source}'}, {'{externalId}'}, {'{timestamp}'}
              </p>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ─── Flow connector ───────────────────────────────────────────────── */}
      <FlowConnector />

      {/* ─── Section 2: Alert Grouping ────────────────────────────────────── */}
      <SectionCard
        title="Alert Grouping"
        description="Group related alerts into a single incident. Reduces noise and provides a consolidated view of related problems."
      >
        {/* Strategy */}
        <FieldGroup label="Grouping Strategy">
          <StrategySelector<GroupingStrategy>
            options={GROUPING_STRATEGY_OPTIONS}
            value={grouping.strategy}
            onChange={handleGroupingStrategyChange}
            disabled={disabled || !grouping.enabled}
          />
        </FieldGroup>

        {/* Group-by fields (content & intelligent only) */}
        {showGroupByFields && (
          <FieldGroup
            label="Group By Fields"
            helpText="Alerts matching on all selected fields will be grouped into the same incident."
          >
            <FieldTagList
              fields={groupingRules}
              onChange={handleGroupByFieldsChange}
              suggestions={suggestions}
              placeholder="Add a group-by field…"
              disabled={disabled || !grouping.enabled}
            />
          </FieldGroup>
        )}

        {/* Time window */}
        <FieldGroup label="Grouping Window">
          <TimeWindowInput
            value={grouping.timeWindowMinutes}
            onChange={handleGroupingTimeWindowChange}
            disabled={disabled || !grouping.enabled}
          />
        </FieldGroup>

        {/* Max alerts per group */}
        <FieldGroup
          label="Maximum Alerts Per Group"
          helpText="Maximum number of alerts that can be grouped together. Set to 0 for unlimited."
        >
          <input
            type="number"
            min={0}
            value={grouping.maxAlertsPerGroup}
            onChange={handleMaxAlertsChange}
            disabled={disabled || !grouping.enabled}
            className="input w-32 tabular-nums"
          />
        </FieldGroup>

        {/* Auto-merge resolved */}
        <div className="flex items-start gap-3">
          <input
            id={autoMergeId}
            type="checkbox"
            checked={grouping.autoMergeResolved}
            onChange={handleAutoMergeResolvedChange}
            disabled={disabled || !grouping.enabled}
            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-400"
          />
          <div>
            <label
              htmlFor={autoMergeId}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Auto-merge Resolve Signals
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When enabled, resolution signals (e.g., &quot;Resolved&quot;
              alerts) will be automatically merged into the existing group
              instead of creating new incidents.
            </p>
          </div>
        </div>
      </SectionCard>
      </div>
    </div>
  );
}
